import "./COREMenu.less";
import React from "react";
import { Menu, MenuItemProps } from "antd";
import { TestID } from "../../shared/testids/testids";
import classNames from "classnames";
import { CORETypographyInput } from "../Typography/CORETypographyInput";
import { NavLink } from "react-router-dom";
import { MenuInfo } from "rc-menu/lib/interface";

interface COREMenuItemProps {
  testID: TestID;
  iconAlign?: "left" | "right";
  className?: string;
  badge?: React.ReactNode;
  to?: string;
  href?: string;
  target?: React.HTMLAttributeAnchorTarget;
  /**
   * Forces the item to render with the selected appearance regardless of the
   * parent menu's `selectedKeys`. Use this to reflect a default choice inside a
   * dropdown popup without marking the dropdown's title as the active tab
   * (adding the key to `selectedKeys` would also select the parent submenu).
   */
  selected?: boolean;
}

interface TitleProps {
  icon: React.ReactNode;
  children?: React.ReactNode;
}

const Title: React.FC<TitleProps> = ({ icon, children }) => {
  if (icon) {
    return (
      <span>
        {children}
        <span className={"icon-right"}>{icon}</span>
      </span>
    );
  }
  return <>{children}</>;
};

export const COREMenuItem: React.FC<COREMenuItemProps & MenuItemProps> = ({
  testID,
  iconAlign = "left",
  icon,
  className,
  children,
  badge,
  disabled,
  to,
  href,
  target,
  onClick,
  selected = false,
  ...props
}) => {
  if (iconAlign === "right") {
    children = <Title icon={icon}>{children}</Title>;
    icon = null;
  }

  const content = (
    <>
      <CORETypographyInput type={"default"}>{children}</CORETypographyInput>
      {badge !== undefined && badge}
    </>
  );

  const linkTo = disabled ? undefined : to;
  const linkHref = disabled ? undefined : href;

  const isModifiedEvent = (domEvent: MenuInfo["domEvent"]) =>
    domEvent.metaKey ||
    domEvent.ctrlKey ||
    domEvent.shiftKey ||
    domEvent.altKey;

  const handleOnClick: MenuItemProps["onClick"] = (info) => {
    if ((linkTo || linkHref) && isModifiedEvent(info.domEvent)) {
      return;
    }
    onClick?.(info);
  };

  const renderContent = () => {
    if (linkTo) {
      return (
        <NavLink to={linkTo} target={target} className="core-menu-item-link">
          {content}
        </NavLink>
      );
    }

    if (linkHref) {
      return (
        <a
          href={linkHref}
          target={target}
          rel={target === "_blank" ? "noopener noreferrer" : undefined}
          className="core-menu-item-link"
        >
          {content}
        </a>
      );
    }

    return content;
  };

  return (
    <Menu.Item
      {...props}
      disabled={disabled}
      onClick={handleOnClick}
      data-testid={testID}
      icon={icon}
      className={classNames(className, "core-menu-item", {
        "ant-menu-item-selected": selected,
      })}
    >
      {renderContent()}
    </Menu.Item>
  );
};
